import { Fragment, type ReactNode, type UIEvent, useCallback, useEffect, useRef } from 'react';
import { type Key, ListBox, type Selection } from 'react-aria-components';
import {
  MultiSelectOption,
  type MultiSelectOptionType,
} from '#components/multi-select/multi-select-option/multi-select-option';
import { OptionDivider } from '#components/form-elements/options/option-divider';
import styles from './multi-select-list.module.css';

type MultiSelectListAccessibleNameProps =
  | { 'aria-label': string; 'aria-labelledby'?: never }
  | { 'aria-label'?: never; 'aria-labelledby': string };

/**
 * Distance from the bottom of the list, in pixels, at which the next page is requested. Roughly
 * two option rows, so the page lands before the user reaches the end.
 */
const LOAD_MORE_THRESHOLD_PX = 80;

export type MultiSelectListProps<T extends MultiSelectOptionType> = {
  items: T[];
  selectedKeys: Set<Key>;
  lastRecommendedIndex: number;
  emptyMessage?: string;
  onSelectionChange: (keys: Selection) => void;
  onItemToggle?: (itemKey: string) => void;
  renderOption?: (item: T) => ReactNode;
  isOpen: boolean;
  optionClass?: string;
  onLoadMore?: () => void;
  hasMore?: boolean;
} & MultiSelectListAccessibleNameProps;

export function MultiSelectList<T extends MultiSelectOptionType>({
  items,
  selectedKeys,
  lastRecommendedIndex,
  emptyMessage,
  onSelectionChange,
  onItemToggle,
  renderOption,
  isOpen,
  optionClass,
  onLoadMore,
  hasMore = false,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: MultiSelectListProps<T>): ReactNode {
  const listBoxRef = useRef<HTMLDivElement>(null);
  const allItemsAreRecommended = items.length > 0 && items.every(item => item.recommended);
  const shouldShowDivider = lastRecommendedIndex >= 0 && !allItemsAreRecommended;

  useEffect(() => {
    if (isOpen && listBoxRef.current) {
      listBoxRef.current.focus();
    }
  }, [isOpen]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>): void => {
      if (!onLoadMore || !hasMore) {
        return;
      }
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight <= LOAD_MORE_THRESHOLD_PX) {
        onLoadMore();
      }
    },
    [onLoadMore, hasMore]
  );

  return (
    <ListBox
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={styles.optionList}
      escapeKeyBehavior="none"
      onScroll={handleScroll}
      onSelectionChange={onSelectionChange}
      ref={listBoxRef}
      renderEmptyState={() =>
        emptyMessage ? <div className={styles.noResults}>{emptyMessage}</div> : null
      }
      selectedKeys={selectedKeys}
      selectionMode="multiple"
    >
      {items.map((item, index) => (
        <Fragment key={item.key}>
          <MultiSelectOption
            className={optionClass}
            item={item}
            onToggle={onItemToggle}
            renderOption={renderOption}
          />
          {index === lastRecommendedIndex && shouldShowDivider ? <OptionDivider /> : null}
        </Fragment>
      ))}
    </ListBox>
  );
}
